import ArticleShell from "@/components/site/ArticleShell";

const BodyEs = () => (
  <div className="article-body">
    <p>Un año después del alta oncológica te dijeron: <em>"ahora empieza el tratamiento adyuvante: tamoxifeno (o letrozol, o anastrozol, o exemestano) durante 5 a 10 años"</em>. Y en muchas consultas te lo presentaron como si fuera un simple "sigue tomando esta pastilla".</p>
    <p>Pero llevas seis meses tomándola y notas cosas que no encajan. Estás <strong>más irritable</strong>. Duermes mal. Aparecen sofocos que traen ansiedad. Lloras por cosas que no deberían tanto. Y cuando lo comentas te dicen que es "por los cambios hormonales", como si eso lo explicara todo y lo hiciera manejable.</p>
    <p>Este artículo es para poner nombre y contexto a la <strong>ansiedad asociada a la hormonoterapia oncológica</strong>. Existe. Está documentada. No estás inventándotelo.</p>

    <h2>Qué está haciendo la hormonoterapia en tu cuerpo</h2>
    <p>Los tratamientos endocrinos actúan bajando drásticamente los niveles de estrógeno circulante (o bloqueando su receptor). Y el estrógeno no solo regula el ciclo menstrual: <strong>modula neurotransmisores cerebrales</strong> asociados al estado de ánimo, al sueño y a la regulación emocional.</p>
    <ul>
      <li><strong>Serotonina</strong>: cuando baja el estrógeno, baja la disponibilidad de serotonina. Aumenta la irritabilidad y la ansiedad.</li>
      <li><strong>GABA</strong>: neurotransmisor calmante. Los estrógenos potencian su acción. Menos estrógeno = menos "freno" natural del sistema nervioso.</li>
      <li><strong>Cortisol</strong>: se altera la curva de cortisol diario. Muchas pacientes describen "despertarse ya cansada y con el corazón acelerado".</li>
      <li><strong>Sueño</strong>: los sofocos nocturnos rompen el sueño profundo. La privación crónica de sueño amplifica la ansiedad.</li>
    </ul>
    <p>No es "estar exagerando por las hormonas". Es una <strong>modificación farmacológica del sistema nervioso</strong> sostenida durante años. Con motivo clínico —reducir riesgo de recidiva— pero con coste real.</p>

    <h2>Cómo se manifiesta</h2>
    <ul>
      <li><strong>Ansiedad basal</strong>: tensión mantenida durante el día que antes no tenías. Sensación de "estar en alerta baja".</li>
      <li><strong>Sofocos con ansiedad</strong>: no todos los sofocos son iguales. Muchos vienen acompañados de taquicardia y sensación de amenaza — se parecen a mini-ataques de pánico.</li>
      <li><strong>Insomnio de mantenimiento</strong>: te duermes bien pero te despiertas a las 3-4 AM y ya no vuelves a dormir.</li>
      <li><strong>Irritabilidad y llanto fácil</strong>: reacción emocional desproporcionada a estímulos pequeños.</li>
      <li><strong>Brain fog</strong>: dificultad para concentrarte, palabras que no salen. Suele empeorar la ansiedad porque duda de "si te está pasando algo".</li>
      <li><strong>Dolor articular</strong>: común con inhibidores de aromatasa. El dolor crónico sostenido dispara ansiedad por sí mismo.</li>
    </ul>

    <h2>Herramientas que sí funcionan (con evidencia)</h2>

    <h3>1 · Regulación específica para sofoco-ansioso</h3>
    <p>Cuando notas que empieza un sofoco: no lo pelees. Al contrario:</p>
    <ul>
      <li>Para lo que estés haciendo si puedes.</li>
      <li>Exhalación 4-6 durante todo el sofoco (2-4 min).</li>
      <li>Refresca cara y cuello (agua o abanico).</li>
      <li>Nombra: <em>"esto es un sofoco. Va a pasar. En 3 minutos estará bajando"</em>.</li>
    </ul>
    <p>Pelear con el sofoco lo amplifica y añade capa de ansiedad. Acompañarlo lo desactiva antes.</p>

    <h3>2 · Higiene del sueño oncológica</h3>
    <ul>
      <li><strong>Temperatura fría</strong> en el dormitorio (18-19°C). Los sofocos nocturnos empeoran con calor.</li>
      <li><strong>Ropa técnica</strong> (bambú, tencel): reducen el impacto del sofoco.</li>
      <li><strong>Cero pantallas</strong> 45 min antes de dormir. La luz azul potencia el brain fog.</li>
      <li>Si te despiertas a las 3 AM y llevas 20 min sin volver a dormir: levántate, práctica breve de respiración, vuelve. No te tortures con las horas.</li>
    </ul>

    <h3>3 · Movimiento suave regular</h3>
    <p>Yoga oncológico o restaurativo 2-3 veces por semana: reduce ansiedad basal, mejora sueño y disminuye dolor articular en pacientes con inhibidores de aromatasa (evidencia SIO-ASCO). No cardio intenso — este suele empeorar los sofocos.</p>

    <h3>4 · Habla con tu oncólogo, sin miedo</h3>
    <p>Si la ansiedad te está machacando la vida, dilo. Opciones a plantearle:</p>
    <ul>
      <li><strong>Cambio de fármaco dentro de la misma familia</strong>: si estás con letrozol y no lo toleras, quizás anastrozol o exemestano te sienten mejor.</li>
      <li><strong>Tratamiento sintomático</strong> (SSRI a dosis bajas, gabapentina para sofocos, melatonina para sueño): son <strong>compatibles</strong> con la mayoría de hormonoterapias.</li>
      <li><strong>Derivación a psicooncología</strong>: cubierta por la sanidad pública en la mayoría de comunidades autónomas.</li>
    </ul>
    <p><strong>No abandones el tratamiento sin hablarlo</strong>. La adherencia a la hormonoterapia reduce significativamente el riesgo de recidiva; abandonarla es una decisión clínica, no un desahogo.</p>

    <h2>Qué NO ayuda</h2>
    <ul>
      <li>Fitoestrógenos "naturales" sin consultar (isoflavonas de soja, cimicifuga, aceite de onagra): en cáncer hormonodependiente pueden estar contraindicados. Consulta siempre con tu oncólogo.</li>
      <li>Café en exceso: potencia ansiedad y sofocos.</li>
      <li>Alcohol para dormir: rompe la fase REM y empeora sofocos nocturnos.</li>
      <li>Aguantar en silencio: la ansiedad no tratada durante 5-10 años erosiona muchísimo la vida.</li>
    </ul>

    <h2>Lecturas relacionadas</h2>
    <ul>
      <li><a href="/blog/ansiedad-despues-cancer">Ansiedad después del cáncer: por qué persiste y cómo trabajar con ella</a></li>
      <li><a href="/blog/fatiga-cronica-despues-cancer-mama">Fatiga crónica después del cáncer de mama</a></li>
      <li><a href="/blog/recuperar-confianza-cuerpo-despues-cancer">Recuperar la confianza en el cuerpo después del cáncer</a></li>
    </ul>

    <p>Si estás en hormonoterapia y la ansiedad te limita, la <a href="/sesion-gratuita">primera sesión conmigo es gratuita</a>. Un espacio para ver qué se puede sostener y qué no, sin culpa.</p>
    <p>— Marta Montardit</p>
  </div>
);

const BodyCa = () => (
  <div className="article-body">
    <p>Un any després de l'alta oncològica et van dir: <em>"ara comença el tractament adjuvant: tamoxifèn (o letrozol, o anastrozol, o exemestà) durant 5 a 10 anys"</em>. I en moltes consultes t'ho van presentar com si fos un simple "segueix prenent aquesta pastilla".</p>
    <p>Però portes sis mesos prenent-la i notes coses que no encaixen. Estàs <strong>més irritable</strong>. Dorms malament. Apareixen sufocacions que porten ansietat. Plores per coses que no haurien de tant. I quan ho comentes et diuen que és "pels canvis hormonals", com si això ho expliqués tot i ho fes manejable.</p>
    <p>Aquest article és per posar nom i context a l'<strong>ansietat associada a l'hormonoteràpia oncològica</strong>. Existeix. Està documentada. No t'ho estàs inventant.</p>

    <h2>Què està fent l'hormonoteràpia al teu cos</h2>
    <p>Els tractaments endocrins actuen abaixant dràsticament els nivells d'estrogen circulant (o bloquejant el seu receptor). I l'estrogen no només regula el cicle menstrual: <strong>modula neurotransmissors cerebrals</strong> associats a l'estat d'ànim, al son i a la regulació emocional.</p>
    <ul>
      <li><strong>Serotonina</strong>: quan baixa l'estrogen, baixa la disponibilitat de serotonina. Augmenta la irritabilitat i l'ansietat.</li>
      <li><strong>GABA</strong>: neurotransmissor calmant. Els estrògens potencien la seva acció. Menys estrogen = menys "fre" natural del sistema nerviós.</li>
      <li><strong>Cortisol</strong>: s'altera la corba de cortisol diari. Moltes pacients descriuen "despertar-se ja cansada i amb el cor accelerat".</li>
      <li><strong>Son</strong>: les sufocacions nocturnes trenquen el son profund. La privació crònica de son amplifica l'ansietat.</li>
    </ul>
    <p>No és "estar exagerant per les hormones". És una <strong>modificació farmacològica del sistema nerviós</strong> sostinguda durant anys. Amb motiu clínic —reduir risc de recidiva— però amb cost real.</p>

    <h2>Com es manifesta</h2>
    <ul>
      <li><strong>Ansietat basal</strong>: tensió mantinguda durant el dia que abans no tenies. Sensació d'"estar en alerta baixa".</li>
      <li><strong>Sufocacions amb ansietat</strong>: no totes les sufocacions són iguals. Moltes vénen acompanyades de taquicàrdia i sensació d'amenaça — s'assemblen a mini-atacs de pànic.</li>
      <li><strong>Insomni de manteniment</strong>: t'adorms bé però et despertes a les 3-4 AM i ja no tornes a dormir.</li>
      <li><strong>Irritabilitat i plor fàcil</strong>: reacció emocional desproporcionada a estímuls petits.</li>
      <li><strong>Brain fog</strong>: dificultat per concentrar-te, paraules que no surten. Sol empitjorar l'ansietat perquè dubta de "si t'està passant alguna cosa".</li>
      <li><strong>Dolor articular</strong>: comú amb inhibidors d'aromatasa. El dolor crònic sostingut dispara ansietat per si mateix.</li>
    </ul>

    <h2>Eines que sí funcionen (amb evidència)</h2>

    <h3>1 · Regulació específica per a sufocació ansiosa</h3>
    <p>Quan notes que comença una sufocació: no la lluitis. Al contrari:</p>
    <ul>
      <li>Atura't del que estàs fent si pots.</li>
      <li>Exhalació 4-6 durant tota la sufocació (2-4 min).</li>
      <li>Refresca cara i coll (aigua o ventall).</li>
      <li>Anomena: <em>"això és una sufocació. Passarà. En 3 minuts estarà baixant"</em>.</li>
    </ul>
    <p>Lluitar amb la sufocació l'amplifica i afegeix capa d'ansietat. Acompanyar-la la desactiva abans.</p>

    <h3>2 · Higiene del son oncològica</h3>
    <ul>
      <li><strong>Temperatura freda</strong> al dormitori (18-19°C). Les sufocacions nocturnes empitjoren amb calor.</li>
      <li><strong>Roba tècnica</strong> (bambú, tencel): redueixen l'impacte de la sufocació.</li>
      <li><strong>Zero pantalles</strong> 45 min abans de dormir. La llum blava potencia el brain fog.</li>
      <li>Si et despertes a les 3 AM i portes 20 min sense tornar a dormir: aixeca't, pràctica breu de respiració, torna. No et torturis amb les hores.</li>
    </ul>

    <h3>3 · Moviment suau regular</h3>
    <p>Ioga oncològic o restauratiu 2-3 vegades per setmana: redueix ansietat basal, millora son i disminueix dolor articular en pacients amb inhibidors d'aromatasa (evidència SIO-ASCO). No cardio intens — aquest sol empitjorar les sufocacions.</p>

    <h3>4 · Parla amb el teu oncòleg, sense por</h3>
    <p>Si l'ansietat t'està destrossant la vida, digues-ho. Opcions a plantejar-li:</p>
    <ul>
      <li><strong>Canvi de fàrmac dins de la mateixa família</strong>: si estàs amb letrozol i no el toleres, potser anastrozol o exemestà et sentin millor.</li>
      <li><strong>Tractament simptomàtic</strong> (SSRI a dosis baixes, gabapentina per a sufocacions, melatonina per al son): són <strong>compatibles</strong> amb la majoria d'hormonoteràpies.</li>
      <li><strong>Derivació a psicooncologia</strong>: coberta per la sanitat pública a la majoria de comunitats autònomes.</li>
    </ul>
    <p><strong>No abandonis el tractament sense parlar-ho</strong>. L'adherència a l'hormonoteràpia redueix significativament el risc de recidiva; abandonar-la és una decisió clínica, no un desfogament.</p>

    <h2>Què NO ajuda</h2>
    <ul>
      <li>Fitoestrògens "naturals" sense consultar (isoflavones de soja, cimicifuga, oli d'onagra): en càncer hormonodependent poden estar contraindicats. Consulta sempre amb el teu oncòleg.</li>
      <li>Cafè en excés: potencia ansietat i sufocacions.</li>
      <li>Alcohol per dormir: trenca la fase REM i empitjora sufocacions nocturnes.</li>
      <li>Aguantar en silenci: l'ansietat no tractada durant 5-10 anys erosiona moltíssim la vida.</li>
    </ul>

    <h2>Lectures relacionades</h2>
    <ul>
      <li><a href="/blog/ansiedad-despues-cancer">Ansietat després del càncer: per què persisteix i com treballar-la</a></li>
      <li><a href="/blog/fatiga-cronica-despues-cancer-mama">Fatiga crònica després del càncer de mama</a></li>
      <li><a href="/blog/recuperar-confianza-cuerpo-despues-cancer">Recuperar la confiança en el cos després del càncer</a></li>
    </ul>

    <p>Si estàs en hormonoteràpia i l'ansietat et limita, la <a href="/sesion-gratuita">primera sessió amb mi és gratuïta</a>. Un espai per veure què es pot sostenir i què no, sense culpa.</p>
    <p>— Marta Montardit</p>
  </div>
);

const ArticleAnsiedadHormonoterapia = () => (
  <ArticleShell
    bodyEs={<BodyEs />}
    bodyCa={<BodyCa />}
    leadMagnet={{
      guide: "post-cancer",
      source: "blog/ansiedad-hormonoterapia",
      title: {
        es: "Guía: Volver a habitar el cuerpo después del cáncer",
        ca: "Guia: Tornar a habitar el cos després del càncer",
      },
      subtitle: {
        es: "Prácticas cuerpo-mente para las semanas y meses tras el tratamiento.",
        ca: "Pràctiques cos-ment per a les setmanes i mesos després del tractament.",
      },
      bullets: {
        es: [
          "Práctica para acompañar un sofoco sin luchar contra él",
          "Higiene del sueño para dormir mejor en hormonoterapia",
          "La recibes en tu email en menos de 5 minutos",
        ],
        ca: [
          "Pràctica per acompanyar una sufocació sense lluitar-hi",
          "Higiene del son per dormir millor en hormonoteràpia",
          "La reps al teu email en menys de 5 minuts",
        ],
      },
    }}
  />
);

export default ArticleAnsiedadHormonoterapia;
